/**
 * @file interpret/components/PlaybackCaption.tsx
 * @brief The translation's caption, word by word with the audio: web port of
 *        FRONTEND `S2SInterpreter/components/PlaybackCaption.tsx`.
 *
 * @details
 * DES 02-10-2026: on the web the caption appeared all at once; on the phone it
 * types itself out ("jaisay type ho rahi ho"). Same timings as the phone:
 * - words follow the audio's position (`getProgress`), all of them by 88% of
 *   it so the last ones are never eaten at the end; a 50 ms tick;
 * - each new word fades in over 300 ms, rising a little;
 * - when the audio ends, any words left appear, the whole caption holds
 *   750 ms, then lifts 24px and fades over 320 ms;
 * - a window of 3 clear lines with a 1.5-line fade above, content anchored to
 *   the bottom, so older lines dissolve as new ones arrive.
 * Before the audio starts (fetching / decoding) no words show. A player that
 * cannot say where it is gets the phone's fallback: 11 characters a second.
 *
 * @author DES
 * @date 02-10-2026
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { isRTLText } from "@/shared/utils/formatters";

const FONT_SIZE = 21;
const SYNC_TICK_MS = 50;
const CAPTION_LEAD = 0.88;
const HOLD_FULL_MS = 750;
const FLOAT_MS = 320;
const LIFT = 24;
const VISIBLE_LINES = 3;
const TOP_FADE_LINES = 1.5;
const FALLBACK_CHARS_PER_SEC = 11;
/** No position from the player this long after a caption arrives: use the
 *  character-rate clock instead. */
const FALLBACK_AFTER_MS = 1500;

type Phase = "revealing" | "hold" | "float" | "done";

export type CaptionProgress = { currentSec: number; durationSec: number } | null;

function splitWords(text: string): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  return words.length > 0 ? words : text ? [text] : [];
}

function wordCountForProgress(count: number, currentSec: number, durationSec: number): number {
  if (count === 0) return 0;
  if (durationSec < 0.2) return count;
  const ratio = Math.min(1, Math.max(0, currentSec / durationSec));
  if (ratio <= 0) return 0;
  const led = Math.min(1, ratio / CAPTION_LEAD);
  return Math.min(count, Math.max(1, Math.ceil(led * count)));
}

export function PlaybackCaption({
  text,
  lang,
  style,
  getProgress,
  onVisibleChange,
}: {
  /** The translation playing now; "" once it has finished. */
  text: string;
  lang: string | null;
  style?: CSSProperties;
  getProgress: () => CaptionProgress;
  /** True from the first word until the caption has floated away. */
  onVisibleChange: (visible: boolean) => void;
}) {
  const trimmed = text.trim();
  const [shown, setShown] = useState("");
  const [shownLang, setShownLang] = useState<string | null>(null);
  const [shownStyle, setShownStyle] = useState<CSSProperties | undefined>(undefined);
  const [visible, setVisible] = useState(0);
  const [phase, setPhase] = useState<Phase>("done");
  const startedRef = useRef(false);
  const arrivedAtRef = useRef(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const progressRef = useRef(getProgress);
  progressRef.current = getProgress;

  const clearTimers = () => {
    for (const t of timers.current) clearTimeout(t);
    timers.current = [];
  };

  // A new translation replaces whatever is showing and starts from no words.
  useEffect(() => {
    if (!trimmed) return;
    clearTimers();
    setShown(trimmed);
    setShownLang(lang);
    setShownStyle(style);
    setVisible(0);
    setPhase("revealing");
    startedRef.current = false;
    arrivedAtRef.current = Date.now();
    // `style` is a fresh object each render; the text decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, lang]);

  const words = splitWords(shown);
  const count = words.length;

  // Words follow the audio.
  useEffect(() => {
    if (phase !== "revealing" || count === 0) return;
    const tick = () => {
      const p = progressRef.current();
      let target: number | null = null;
      if (p && p.durationSec > 0.2) {
        startedRef.current = true;
        target = wordCountForProgress(count, p.currentSec, p.durationSec);
      } else if (Date.now() - arrivedAtRef.current > FALLBACK_AFTER_MS) {
        startedRef.current = true;
        const since = (Date.now() - arrivedAtRef.current - FALLBACK_AFTER_MS) / 1000;
        const estimate = Math.max(2.2, shown.length / FALLBACK_CHARS_PER_SEC);
        target = wordCountForProgress(count, since, estimate);
      }
      if (target !== null) setVisible((v) => (target! > v ? target! : v));
    };
    tick();
    const id = setInterval(tick, SYNC_TICK_MS);
    return () => clearInterval(id);
  }, [phase, count, shown]);

  // The audio ended: the rest at once, hold, float away.
  useEffect(() => {
    if (trimmed || phase !== "revealing") return;
    if (!startedRef.current) {
      setPhase("done");
      return;
    }
    setVisible(count);
    setPhase("hold");
    timers.current.push(
      setTimeout(() => {
        setPhase("float");
        timers.current.push(setTimeout(() => setPhase("done"), FLOAT_MS));
      }, HOLD_FULL_MS),
    );
  }, [trimmed, phase, count]);

  useEffect(() => () => clearTimers(), []);

  const isVisible = phase !== "done" && !!shown;
  useEffect(() => {
    onVisibleChange(isVisible);
  }, [isVisible, onVisibleChange]);

  if (!isVisible) return null;

  const lineFactor = typeof shownStyle?.lineHeight === "number" ? shownStyle.lineHeight : 1.375;
  const lineHeight = Math.round(FONT_SIZE * lineFactor);
  const fadeH = Math.round(lineHeight * TOP_FADE_LINES);
  const rtl = isRTLText(shown);

  return (
    <div
      className="pointer-events-none absolute left-1/2 top-1/2 w-[20rem] max-w-[calc(100vw-3rem)]"
      style={{
        height: fadeH + lineHeight * VISIBLE_LINES,
        opacity: phase === "float" ? 0 : 1,
        transform: `translate(-50%, calc(-50% - ${phase === "float" ? LIFT : 0}px))`,
        transition: `opacity ${FLOAT_MS}ms cubic-bezier(0.33, 1, 0.68, 1), transform ${FLOAT_MS}ms cubic-bezier(0.33, 1, 0.68, 1)`,
      }}
    >
      <div
        className="flex h-full flex-col justify-end overflow-hidden"
        style={{
          WebkitMaskImage: `linear-gradient(to bottom, transparent 0, #000 ${fadeH}px)`,
          maskImage: `linear-gradient(to bottom, transparent 0, #000 ${fadeH}px)`,
        }}
      >
        <p
          lang={shownLang ?? undefined}
          dir={rtl ? "rtl" : "ltr"}
          aria-live="polite"
          aria-label={shown}
          className="text-center font-semibold text-foreground"
          style={{ ...shownStyle, fontSize: FONT_SIZE, lineHeight: `${lineHeight}px` }}
        >
          {words.slice(0, visible).map((w, i) => (
            <span key={i}>
              <span className="inline-block duration-300 animate-in fade-in-0 slide-in-from-bottom-1">
                {w}
              </span>
              {i < visible - 1 ? " " : null}
            </span>
          ))}
        </p>
      </div>
    </div>
  );
}
